import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { recruitmentTheme, themeClasses, getStatusColor } from '../styles/theme';
import { subscribeToTasks, updateTask as updateTaskInFirebase, Task as FirebaseTask } from '../services/taskService';
import { StatusBadge, taskStatusOptions, priorityOptions } from '../components/ui/StatusBadge';
import CandidateStatusBadge from '../components/ui/CandidateStatusBadge';
import { enhancedSearch } from '../utils/searchUtils';
import { LocalStorageManager } from '../utils/localStorage';
import { useNotifications } from '../contexts/NotificationContext';
import { subscribeToCandidates } from '../services/candidateService';
import { subscribeToJobs } from '../services/jobService';
import { CandidateStage } from '../types';
import { 
  CheckSquare, 
  Clock, 
  AlertCircle, 
  Plus, 
  Search, 
  Filter,
  Calendar,
  User,
  Users,
  Target,
  CheckCircle,
  X,
  Save,
  ArrowUpDown,
  Trash2,
  Edit,
  Archive,
  GripVertical,
  Settings,
  Briefcase,
  ArrowRight
} from 'lucide-react';

// Use Firebase Task interface
type Task = FirebaseTask;

interface Candidate {
  id: string;
  _id?: string;
  name: string;
  email: string;
  phone: string;
  jobTitle?: string;
  company?: string;
  salary?: number;
  stage: CandidateStage;
  city?: string;
  countryApplyingTo?: string;
  passportNumber?: string;
  createdAt?: string;
}

interface RecentJob {
  id?: string;
  title: string;
  company: string;
  location: string;
  status: string;
  type?: string;
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  postedDate?: string;
}

type ColumnConfig = {
  [key: string]: {
    title: string;
    width: string;
    sortable: boolean;
    sortField?: keyof Task;
  };
};

const columnConfig: ColumnConfig = {
  select: { title: '', width: 'w-12', sortable: false },
  task: { title: 'Task', width: '', sortable: true, sortField: 'title' },
  assignedTo: { title: 'Assigned To', width: '', sortable: true, sortField: 'assignedTo' },
  dueDate: { title: 'Due Date', width: '', sortable: true, sortField: 'dueDate' },
  priority: { title: 'Priority', width: '', sortable: true, sortField: 'priority' },
  category: { title: 'Category', width: '', sortable: true, sortField: 'category' },
  status: { title: 'Status', width: '', sortable: false },
};

const AssociatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { notifyBadgeChange, notifyContentChange } = useNotifications();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [recentCandidates, setRecentCandidates] = useState<Candidate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState([
    'select', 'task', 'assignedTo', 'dueDate', 'priority', 'category', 'status'
  ]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: '',
    priority: 'MEDIUM',
    category: ''
  });

  const statusOptions = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];
  const localPriorityOptions = ['LOW', 'MEDIUM', 'HIGH'];
  const categoryOptions = ['Follow-up', 'Screening', 'Interview', 'Data Entry', 'Medical', 'Documentation'];

  // Helper functions
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700 border-green-200';
      case 'Closed': return 'bg-red-100 text-red-700 border-red-200';
      case 'Draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Currency formatting function
  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  // Data loading functions
  const loadCandidates = () => {
    const candidates = LocalStorageManager.getCandidates();
    // Keep ALL candidates for accurate counts - don't slice
    setRecentCandidates(candidates);
  };

  const loadJobs = () => {
    const jobs = LocalStorageManager.getJobs();
    setRecentJobs(jobs);
  };

  // Subscribe to real-time updates from Firebase
  useEffect(() => {
    let unsubscribeTasks: (() => void) | null = null;
    let unsubscribeCandidates: (() => void) | null = null;
    let unsubscribeJobs: (() => void) | null = null;
    
    const setupAllSubscriptions = async () => {
      try {
        console.log('🤝 Associates: Setting up Firebase subscriptions...');
        
        // Subscribe to tasks
        unsubscribeTasks = await subscribeToTasks((firebaseTasks) => {
          console.log('🤝 Associates: Received', firebaseTasks.length, 'tasks from Firebase');
          setTasks(firebaseTasks);
        }, console.error);
        
        // Subscribe to candidates
        unsubscribeCandidates = await subscribeToCandidates((firebaseCandidates) => {
          console.log('🤝 Associates: Received', firebaseCandidates.length, 'candidates from Firebase');
          setRecentCandidates(firebaseCandidates);
        }, console.error);
        
        // Subscribe to jobs
        unsubscribeJobs = subscribeToJobs((firebaseJobs) => {
          console.log('🤝 Associates: Received', firebaseJobs.length, 'jobs from Firebase');
          setRecentJobs(firebaseJobs);
        });
        
      } catch (error) {
        console.error('🤝 Associates: Failed to setup Firebase subscriptions:', error);
      }
    };
    
    setupAllSubscriptions();
    
    // Listen for localStorage changes and comprehensive data updates
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith('recruitment_crm_') || e.key === 'generatedJobs' || e.key === 'addedCandidates' || e.key === 'cvBuilderCandidates') {
        console.log('🤝 Associates: Storage change detected for key:', e.key);
        if (e.key === 'generatedJobs' || e.key?.includes('job')) {
          loadJobs();
        }
        if (e.key?.includes('candidate') || e.key === 'addedCandidates' || e.key === 'cvBuilderCandidates') {
          loadCandidates();
        }
      }
    };

    // Listen for comprehensive data update events for instant sync
    const handleDataUpdates = (event: CustomEvent) => {
      console.log('🤝 Associates: Data update event received:', event.type);
      if (event.type.includes('candidate')) {
        loadCandidates();
      }
      if (event.type.includes('job')) {
        loadJobs();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    // Add comprehensive event listeners for instant updates
    window.addEventListener('candidatesUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateAdded', handleDataUpdates as EventListener);
    window.addEventListener('candidateUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateDeleted', handleDataUpdates as EventListener);
    window.addEventListener('jobsUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobAdded', handleDataUpdates as EventListener);
    window.addEventListener('jobUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobDeleted', handleDataUpdates as EventListener);
    
    return () => {
      console.log('🤝 Associates: Cleaning up Firebase subscriptions and event listeners...');
      if (unsubscribeTasks) {
        unsubscribeTasks();
      }
      if (unsubscribeCandidates) {
        unsubscribeCandidates();
      }
      if (unsubscribeJobs) {
        unsubscribeJobs();
      }
      window.removeEventListener('storage', handleStorageChange);
      // Remove all data update event listeners
      window.removeEventListener('candidatesUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateAdded', handleDataUpdates as EventListener);
      window.removeEventListener('candidateUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('jobsUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobAdded', handleDataUpdates as EventListener);
      window.removeEventListener('jobUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobDeleted', handleDataUpdates as EventListener);
    };
  }, []);

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const handleSelectTask = (taskId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks(prev => [...prev, taskId]);
    } else {
      setSelectedTasks(prev => prev.filter(id => id !== taskId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks(
        filteredTasks
          .map((task) => task.id)
          .filter((id): id is string => Boolean(id))
      );
    } else {
      setSelectedTasks([]);
    }
  };

  const handleDeleteSelected = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedTasks.length} task(s)? They will be moved to the recycle bin.`)) {
      // Delete each selected task using LocalStorageManager
      let deletedCount = 0;
      selectedTasks.forEach(taskId => {
        const success = LocalStorageManager.deleteTask(taskId);
        if (success) deletedCount++;
      });
      
      // Reload tasks from LocalStorageManager to ensure consistency
      const updatedTasks = LocalStorageManager.getTasks();
      setTasks(updatedTasks);
      setSelectedTasks([]);
      
      alert(`${deletedCount} task(s) moved to recycle bin!`);
    }
  };

  const handleArchiveCompleted = () => {
    const completedTasks = tasks.filter(task => task.status === 'COMPLETED');
    if (completedTasks.length === 0) {
      alert('No completed tasks to archive.');
      return;
    }
    
    if (window.confirm(`Archive ${completedTasks.length} completed task(s)? They will be moved to the recycle bin.`)) {
      // Delete each completed task using LocalStorageManager
      let archivedCount = 0;
      completedTasks.forEach(task => {
        if (!task.id) {
          return;
        }
        const success = LocalStorageManager.deleteTask(task.id);
        if (success) {
          archivedCount++;
        }
      });
      
      // Reload tasks from LocalStorageManager to ensure consistency
      const updatedTasks = LocalStorageManager.getTasks();
      setTasks(updatedTasks);
      
      alert(`${archivedCount} completed tasks have been moved to recycle bin.`);
    }
  };

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    if (searchTerm) {
      filtered = filtered.filter(task => 
        enhancedSearch(searchTerm, [
          task.title,
          task.description,
          task.assignedTo,
          task.assignedBy,
          task.category,
          task.dueDate,
          task.status,
          task.priority,
          task.id
        ])
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField as keyof Task];
        const bValue = b[sortField as keyof Task];

        // Handle null or undefined values to avoid errors, placing them at the end.
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const comparableA = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
        const comparableB = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

        let comparison = 0;
        if (comparableA > comparableB) {
          comparison = 1;
        } else if (comparableA < comparableB) {
          comparison = -1;
        }

        return sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
  }, [tasks, searchTerm, statusFilter, priorityFilter, sortField, sortDirection]);

  const updateTaskStatus = (taskId: string, newStatus: string) => {
    const mappedStatus = newStatus.toUpperCase().replace('-', '_') as Task['status'];
    const task = tasks.find(t => t.id === taskId);
    const oldStatus = task?.status || 'UNKNOWN';
    const taskTitle = task?.title || 'Unknown Task';
    
    // Update Firebase - the real-time listener will update the UI
    updateTaskInFirebase(taskId, { status: mappedStatus })
      .then(() => {
        console.log(`🤝 Associates: Successfully updated task status: ${oldStatus} → ${mappedStatus}`);
        // Notify badge change
        notifyBadgeChange('Associates Page', `${taskTitle} Status`, oldStatus, mappedStatus);
      })
      .catch(error => {
        console.error('❌ Associates: Failed to update task status in Firebase:', error);
      });
  };

  const updateTaskPriority = (taskId: string, newPriority: string) => {
    const mappedPriority = newPriority.toUpperCase() as Task['priority'];
    const task = tasks.find(t => t.id === taskId);
    const oldPriority = task?.priority || 'UNKNOWN';
    const taskTitle = task?.title || 'Unknown Task';
    
    // Update Firebase - the real-time listener will update the UI
    updateTaskInFirebase(taskId, { priority: mappedPriority })
      .then(() => {
        console.log(`🤝 Associates: Successfully updated task priority: ${oldPriority} → ${mappedPriority}`);
        // Notify badge change
        notifyBadgeChange('Associates Page', `${taskTitle} Priority`, oldPriority, mappedPriority);
      })
      .catch(error => {
        console.error('❌ Associates: Failed to update task priority in Firebase:', error);
      });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      priority: 'MEDIUM',
      category: ''
    });
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const handleAddTask = () => {
    // Note: For Associates page, we'll disable task creation as it should be managed from Tasks page
    alert('Task creation should be done from the Tasks page for proper workflow control.');
    setShowAddModal(false);
  };

  const handleDeleteTask = (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task? It will be moved to the recycle bin.')) {
      return;
    }
    
    // Note: For Associates page, we'll disable task deletion as it should be managed from Tasks page
    alert('Task management (including deletion) should be done from the Tasks page for proper workflow control.');
  };

  const renderTaskCell = (task: Task, columnId: string) => {
    switch (columnId) {
      case 'select':
        return (
          <input
            type="checkbox"
            checked={task.id ? selectedTasks.includes(task.id) : false}
            onChange={(e) => {
              if (task.id) {
                handleSelectTask(task.id, e.target.checked);
              }
            }}
            className="rounded"
          />
        );
      case 'task':
        return (
          <div>
            <div className="font-medium text-[#8b6914]">{task.title}</div>
            <div className="text-sm text-gray-500">{task.description}</div>
          </div>
        );
      case 'assignedTo':
        return (
          <div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{task.assignedTo}</span>
            </div>
            <div className="text-xs text-gray-400">by {task.assignedBy}</div>
          </div>
        );
      case 'dueDate':
        return (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{task.dueDate}</span>
          </div>
        );
      case 'priority':
        return (
          <StatusBadge
            currentStatus={task.priority.toLowerCase()}
            options={priorityOptions}
            onStatusChange={(newPriority) => {
              if (task.id) {
                updateTaskPriority(task.id, newPriority);
              }
            }}
            size="sm"
          />
        );
      case 'category':
        return (
          <StatusBadge
            currentStatus={task.category.toLowerCase().replace(/\s+/g, '-')}
            options={[
              { value: 'follow-up', label: 'Follow-up', color: 'text-blue-700', bgColor: 'bg-blue-100' },
              { value: 'screening', label: 'Screening', color: 'text-green-700', bgColor: 'bg-green-100' },
              { value: 'interview', label: 'Interview', color: 'text-purple-700', bgColor: 'bg-purple-100' },
              { value: 'data-entry', label: 'Data Entry', color: 'text-gray-700', bgColor: 'bg-gray-100' },
              { value: 'medical', label: 'Medical', color: 'text-red-700', bgColor: 'bg-red-100' },
              { value: 'documentation', label: 'Documentation', color: 'text-orange-700', bgColor: 'bg-orange-100' }
            ]}
            onStatusChange={(newCategory) => {
              if (!task.id) {
                return;
              }
              const formattedCategory = newCategory.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              const oldCategory = task.category;
              
              // Update Firebase - the real-time listener will update the UI
              updateTaskInFirebase(task.id, { category: formattedCategory })
                .then(() => {
                  console.log(`🤝 Associates: Successfully updated task category: ${oldCategory} → ${formattedCategory}`);
                  // Notify badge change
                  notifyBadgeChange('Associates Page', `${task.title} Category`, oldCategory, formattedCategory);
                })
                .catch(error => {
                  console.error('❌ Associates: Failed to update task category in Firebase:', error);
                });
            }}
            size="sm"
          />
        );
      case 'status':
        return (
          <StatusBadge
            currentStatus={task.status.toLowerCase().replace('_', '-')}
            options={taskStatusOptions}
            onStatusChange={(newStatus) => {
              if (task.id) {
                updateTaskStatus(task.id, newStatus);
              }
            }}
            size="sm"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Associates Task Management</h1>
          <p className="text-gray-600 mt-1">
            Task management system for associates with full task lifecycle tracking
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate('/cv-builder')}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
          >
            <Plus className="w-4 h-4" />
            <span>Add Candidate</span>
          </button>
          <button 
            onClick={() => navigate('/candidates')}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
          >
            <User className="w-4 h-4" />
            <span>View Candidates</span>
          </button>
          <button 
            onClick={() => navigate('/appointments', { state: { openAddModal: true } })}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
          >
            <Calendar className="w-4 h-4" />
            <span>Book Appointment</span>
          </button>
          <button 
            onClick={() => navigate('/jobs')}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
          >
            <Briefcase className="w-4 h-4" />
            <span>View Jobs</span>
          </button>
        </div>
      </div>

      {/* Recent Candidates */}
      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-[#8b6914]" />
              <CardTitle className="text-[#8b6914]">Recent Candidates</CardTitle>
              <span className="text-sm text-gray-500">({recentCandidates.length} recent)</span>
            </div>
            <button 
              onClick={() => navigate('/candidates')}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
            >
              <span>View All Candidates</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {recentCandidates.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates found</h3>
              <p className="mt-1 text-sm text-gray-500">Add your first candidate to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCandidates.slice(0, 5).map((candidate, index) => (
                <div key={candidate.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                          {index + 1}.
                        </span>
                        <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                      </div>
                      <CandidateStatusBadge
                        currentStatus={candidate.stage}
                        onStatusChange={(newStage) => {
                          const oldStage = candidate.stage;
                          LocalStorageManager.updateCandidate(candidate.id, { stage: newStage });
                          loadCandidates();
                          
                          // Dispatch event to sync with other pages
                          window.dispatchEvent(new CustomEvent('candidatesUpdated', {
                            detail: { 
                              candidateId: candidate.id, 
                              updates: { stage: newStage },
                              action: 'status_change'
                            }
                          }));
                        }}
                        size="sm"
                      />
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                      <span>{candidate.jobTitle || 'No position'}</span>
                      <span>{candidate.company || 'No company'}</span>
                      <span>{formatCurrency(candidate.salary)}</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                      <span>{candidate.email}</span>
                      <span>•</span>
                      <span>{candidate.phone}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </div>

      {/* Recent Jobs */}
      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Briefcase className="w-5 h-5 text-[#8b6914]" />
              <CardTitle className="text-[#8b6914]">Recent Jobs</CardTitle>
              <span className="text-sm text-gray-500">({recentJobs.length})</span>
            </div>
            <button 
              onClick={() => navigate('/jobs')}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
            >
              <span>View All Jobs</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {recentJobs.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">Add your first job posting to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentJobs.slice(0, 5).map((job, index) => (
                <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                          {index + 1}.
                        </span>
                        <h4 className="font-medium text-gray-900">{job.title}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getJobStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                      <span>{job.company}</span>
                      <span>{job.location}</span>
                      <span>{job.type}</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                      <span>{job.salaryRange?.min || 'N/A'} - {job.salaryRange?.max || 'N/A'} {job.salaryRange?.currency || 'KES'}</span>
                      <span>•</span>
                      <span>
                        Posted {job.postedDate ? new Date(job.postedDate).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </div>

      {/* Filters and Search */}
      <div className={recruitmentTheme.components.card}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-[#8b6914]" />
            <h2 className={themeClasses.sectionTitle}>All Tasks</h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tasks, assignees, categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${recruitmentTheme.components.input} pl-10 w-full sm:w-80`}
                data-search="true"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
            
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Priority</option>
              {localPriorityOptions.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedTasks.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700">
              {selectedTasks.length} task(s) selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDeleteSelected}
                className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Selected</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded"
            />
            <span>Select All</span>
          </label>
          
          <button
            onClick={handleArchiveCompleted}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
          >
            <Archive className="w-4 h-4" />
            <span>Archive Completed</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {columnOrder.map((columnId) => {
                  const config = columnConfig[columnId as keyof typeof columnConfig];
                  return (
                    <th 
                      key={columnId}
                      className={`text-left py-3 px-4 font-medium text-[#8b6914] ${config.width} ${
                        columnId !== 'select' && columnId !== 'status' ? 'cursor-move' : ''
                      }`}
                      draggable={columnId !== 'select' && columnId !== 'status'}
                      onDragStart={(e) => handleDragStart(e, columnId)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnId)}
                      style={{
                        backgroundColor: draggedColumn === columnId ? '#f3f4f6' : undefined
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        {columnId !== 'select' && columnId !== 'status' && (
                          <GripVertical className="w-3 h-3 text-gray-400" />
                        )}
                        {columnId === 'select' ? (
                          <input
                            type="checkbox"
                            checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded"
                          />
                        ) : config.sortable ? (
                          <button 
                            onClick={() => handleSort(config.sortField!)}
                            className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                          >
                            <span>{config.title}</span>
                            <ArrowUpDown className="w-3 h-3" />
                          </button>
                        ) : (
                          <span>{config.title}</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task, index) => (
                <tr key={task.id} className="border-b border-gray-100 hover:bg-gray-50">
                  {columnOrder.map((columnId) => (
                    <td key={columnId} className="py-3 px-4">
                      {columnId === 'task' ? (
                        <div className="flex items-start space-x-2">
                          <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                            {index + 1}.
                          </span>
                          <div>
                            <div className="font-medium text-[#8b6914]">{task.title}</div>
                            <div className="text-sm text-gray-500">{task.description}</div>
                          </div>
                        </div>
                      ) : (
                        renderTaskCell(task, columnId)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <CheckSquare className="mx-auto h-12 w-12 text-[#8b6914] opacity-50" />
            <h3 className="mt-2 text-sm font-medium text-[#8b6914]">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first task.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Add New Task</h2>
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Task Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Task details and instructions..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Assigned To *</label>
                <input
                  type="text"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Enter assignee name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Due Date *</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  {localPriorityOptions.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select category</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddTask}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={!formData.title || !formData.assignedTo || !formData.dueDate}
              >
                <Save className="w-4 h-4" />
                <span>Add Task</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssociatesPage;
